import Logo from '../../Assets/nav-logo.png';
import { useRef, useState, useEffect } from 'react';
import { FaFax, FaMoneyCheckAlt, FaChevronRight } from 'react-icons/fa';

const Navbar = () => {
    const navbarData = [
        {
            id: 1,
            title: "Home",
            path: "/"
        },
        {
            id: 2,
            title: "About",
            path: "/about"
        },
        {
            id: 3,
            title: "Services",
            path: "/service"
        },
        {
            id: 5,
            title: "Contact",
            path: "/contact"
        },
        {
            id: 6,
            title: "Blog",
            path: "/blog"
        },
        {
            id: 7,
            title: "FAQ",
            path: "/faq"
        },
    ];

    // Products dropdown items (mega-menu style: icon + title + description)
    const productsData = [
        {
            id: 1,
            title: "CRA FAX Application",
            desc: "Send fax to CRA faster",
            path: "/products/cra-fax",
            icon: <FaFax size={18} />,
            accent: "#D80621"
        },
        {
            id: 2,
            title: "OrientalPay",
            desc: "Efficient Canada payroll",
            path: "/products/oriental-pay",
            icon: <FaMoneyCheckAlt size={18} />,
            accent: "#1B3A5C"
        },
    ];

    // NOTE: Previously this component relied on Bootstrap's JS
    // (data-bs-toggle="collapse") to open/close the mobile menu, and only
    // manipulated the "show" class manually via a ref when a link was
    // clicked. If bootstrap.bundle.min.js wasn't loaded, or React re-rendered
    // and wiped out the class Bootstrap's JS had added, the hamburger button
    // would appear to do nothing. Driving the open/closed state from React
    // instead makes it reliable regardless of whether Bootstrap's JS is wired
    // up correctly.
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const navbarCollapseRef = useRef(null);
    const [scrolled, setScrolled] = useState(false);

    // Because .obs-navbar-wrap is position: fixed, it's taken out of the
    // normal document flow, so the page content underneath it doesn't
    // automatically get pushed down - it renders right under the fixed
    // navbar and gets covered by it. The navbar's own height also changes
    // (96px -> 78px desktop, 80px -> 68px mobile) depending on scroll state,
    // so a single hardcoded padding value can't account for both. Instead we
    // measure the navbar's actual rendered height and render an invisible
    // spacer of that same height right after it, in normal document flow,
    // so the next section always starts right below the navbar rather than
    // underneath it - on every screen size.
    const navRef = useRef(null);
    const [navHeight, setNavHeight] = useState(0);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleOutsideClick = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isDropdownOpen]);

    // Auto-close dropdown when navbar is closed
    useEffect(() => {
        if (!isOpen) {
            setIsDropdownOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 60);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const updateNavHeight = () => {
            if (navRef.current) {
                setNavHeight(navRef.current.offsetHeight);
            }
        };
        updateNavHeight();
        window.addEventListener('resize', updateNavHeight);
        // Height changes with the .scrolled class transition, so re-measure
        // shortly after scroll state flips too (transition is 0.35s).
        const timeout = setTimeout(updateNavHeight, 400);
        return () => {
            window.removeEventListener('resize', updateNavHeight);
            clearTimeout(timeout);
        };
    }, [scrolled, isOpen]);

    // Close the mobile menu automatically if the viewport is resized up to
    // desktop width (e.g. rotating a tablet, or resizing a browser window)
    // so it doesn't stay stuck "open" underneath the desktop layout.
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 992 && isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const handleToggleClick = () => {
        setIsOpen((prev) => !prev);
    };

    const handleLinkClick = () => {
        setIsOpen(false);
        setIsDropdownOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap');

                .obs-navbar-wrap, .obs-navbar-wrap * {
                    font-family: 'Plus Jakarta Sans', 'Segoe UI', sans-serif;
                }
                .obs-navbar-wrap {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    z-index: 999;
                    transition: all 0.35s ease;
                }

                .obs-navbar {
                    background: transparent;
                    height: 96px;
                    display: flex;
                    align-items: center;
                    transition: height 0.35s ease, background 0.35s ease, background-color 0.35s ease,
                        backdrop-filter 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
                    border-bottom: 1px solid transparent;
                }
                .obs-navbar-wrap.scrolled .obs-navbar {
                    background: #ffffff;
                    height: 78px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.06);
                    border-bottom: 1px solid #e5e7eb;
                }

                /* Logo */
                .navbar-brand img {
                    height: 60px;
                    width: auto;
                    transition: filter 0.3s ease, height 0.35s ease;
                    filter: brightness(0) invert(1);
                }
                .obs-navbar-wrap.scrolled .navbar-brand img {
                    filter: none;
                    height: 50px;
                }

                /* Center nav links */
                .obs-navbar .nav-link {
                    font-weight: 600;
                    font-size: 0.92rem;
                    color: rgba(255,255,255,0.92) !important;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 8px 4px !important;
                    transition: color 0.3s ease;
                }
                .obs-navbar-wrap.scrolled .obs-navbar .nav-link {
                    color: #1f2937 !important;
                }
                .obs-navbar .nav-link:hover {
                    color: #FBBF24 !important;
                }
                .obs-navbar-wrap.scrolled .obs-navbar .nav-link:hover {
                    color: #E52629 !important;
                }
                .obs-navbar .dropdown-toggle {
                    font-weight: 600;
                }

                .products-mega {
                    display: none;
                    min-width: 360px;
                    padding: 12px;
                    border: none;
                    border-radius: 14px;
                    box-shadow: 0 20px 45px rgba(0,0,0,0.16);
                    animation: obsFadeSlide 0.18s ease;
                    background: #fff;
                }
                .products-mega.show {
                    display: block !important;
                }
                @keyframes obsFadeSlide {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .products-mega-item {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 14px;
                    border-radius: 10px;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }
                .products-mega-item:hover {
                    background: linear-gradient(135deg, #FDF0ED, #FFF7F0);
                    transform: translateX(4px);
                }
                .products-mega-icon {
                    width: 44px;
                    height: 44px;
                    min-width: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .products-mega-item:hover .products-mega-icon {
                    transform: scale(1.05);
                    box-shadow: 0 6px 16px rgba(46,47,51,0.18);
                }
                .products-mega-title {
                    font-family: 'Plus Jakarta Sans', sans-serif;
                    font-weight: 700;
                    color: #212529;
                    font-size: 0.96rem;
                    margin-bottom: 3px;
                }
                .products-mega-desc {
                    font-family: 'Inter', sans-serif;
                    color: #6b7280;
                    font-size: 0.8rem;
                    font-weight: 400;
                }
                .products-mega-item:hover .products-mega-desc {
                    color: #F47B34;
                }
                .products-mega-arrow {
                    margin-left: auto;
                    color: #C9CDD3;
                    transition: all 0.2s ease;
                }
                .products-mega-item:hover .products-mega-arrow {
                    transform: translateX(4px);
                    color: #E52629;
                }

                /* Right utility pill: Login | Contact us */
                .obs-utility-pill {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    background: rgba(255,255,255,0.14);
                    border: 1px solid rgba(255,255,255,0.25);
                    backdrop-filter: blur(6px);
                    border-radius: 999px;
                    padding: 8px 18px;
                    transition: background 0.3s ease, border-color 0.3s ease;
                }
                .obs-navbar-wrap.scrolled .obs-utility-pill {
                    background: #f3f4f6;
                    border-color: #e5e7eb;
                }
                .obs-utility-link {
                    color: rgba(255,255,255,0.92);
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    white-space: nowrap;
                    transition: color 0.3s ease;
                }
                .obs-navbar-wrap.scrolled .obs-utility-link {
                    color: #374151;
                }
                .obs-utility-link:hover {
                    color: #FBBF24;
                }
                .obs-navbar-wrap.scrolled .obs-utility-link:hover {
                    color: #E52629;
                }
                .obs-utility-divider {
                    width: 1px;
                    height: 16px;
                    background: rgba(255,255,255,0.3);
                }
                .obs-navbar-wrap.scrolled .obs-utility-divider {
                    background: #d1d5db;
                }

                /* CTA buttons */
                .op-demo-btn {
                    background: #FBBF24 !important;
                    color: #000000 !important;
                    font-weight: 700;
                    border-radius: 50px !important;
                    padding: 10px 20px !important;
                    font-size: 0.88rem;
                    border: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s ease;
                    text-decoration: none;
                    white-space: nowrap;
                }
                .op-demo-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(251, 191, 36, 0.35);
                    background: #f59e0b !important;
                }
                .op-trial-btn {
                    background: #1D4ED8 !important;
                    color: #ffffff !important;
                    font-weight: 700;
                    border-radius: 50px !important;
                    padding: 10px 20px !important;
                    font-size: 0.88rem;
                    border: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s ease;
                    text-decoration: none;
                    white-space: nowrap;
                }
                .op-trial-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(29, 78, 216, 0.35);
                    background: #1e40af !important;
                }

                .navbar-toggler {
                    border: 2px solid rgba(255,255,255,0.6) !important;
                    padding: 6px 10px;
                    border-radius: 6px;
                    transition: all 0.3s ease;
                }
                .obs-navbar-wrap.scrolled .navbar-toggler {
                    border: 2px solid #E52629 !important;
                }
                .navbar-toggler-icon {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(255, 255, 255, 1)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e") !important;
                }
                .obs-navbar-wrap.scrolled .navbar-toggler-icon {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(229, 38, 41, 1)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e") !important;
                }

                /* Mobile collapse panel - now driven purely by the .show
                   class React applies, so no Bootstrap JS is required for
                   this to open/close. Kept a max-height + scroll so a tall
                   menu never overflows off-screen on short viewports
                   (e.g. landscape phones). */
                .collapse.navbar-collapse {
                    display: none;
                }
                .collapse.navbar-collapse.show {
                    display: block;
                }
                @media (min-width: 992px) {
                    .collapse.navbar-collapse {
                        display: flex !important;
                    }
                    /* Center the main menu links in the navbar on desktop,
                       independent of how wide the logo or the right-side
                       login pill happen to be. The link list is taken out
                       of normal flex flow and centered against the nav bar
                       itself, so it stays visually centered regardless of
                       the widths on either side of it. */
                    .obs-navbar .container-fluid {
                        position: relative;
                        display: grid;
                        grid-template-columns: 1fr 1fr 1fr;
                        align-items: center;
                    }
                    /* Logo centered in the left third */
                    .obs-navbar .navbar-brand {
                        grid-column: 1;
                        justify-self: center;
                    }
                    /* Toggler only exists for mobile; hide it explicitly at
                       desktop so it can't claim a grid cell. */
                    .obs-navbar .navbar-toggler {
                        display: none;
                    }
                    /* The collapse wrapper's only visible desktop content is
                       the login pill (the link list inside it is pulled out
                       via position:absolute below), so centering the whole
                       wrapper in the right third centers the login pill. */
                    .obs-navbar .collapse.navbar-collapse {
                        grid-column: 3;
                        justify-self: center;
                    }
                    .obs-navbar .obs-center-links {
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        transform: translate(-50%, -50%);
                        margin: 0 !important;
                        display: flex;
                        white-space: nowrap;
                    }
                }

                /* Tablet / small-laptop band: at these widths the seven
                   links + dropdown take up more of the available space
                   relative to the logo and login pill, so a true center
                   could start to crowd them. Tighten spacing and type size
                   here rather than letting it collide; the wider desktop
                   breakpoint below relaxes both back to normal. */
                @media (min-width: 992px) and (max-width: 1199.98px) {
                    .obs-navbar .obs-center-links {
                        gap: 0.85rem !important;
                    }
                    .obs-navbar .obs-center-links .nav-link {
                        font-size: 0.82rem;
                        padding: 8px 2px !important;
                    }
                }

                @media (min-width: 1200px) {
                    .obs-navbar .obs-center-links {
                        gap: 1.5rem !important;
                    }
                }

                @media (max-width: 992px) {
                    .navbar-brand img {
                        height: 55px;
                    }
                    .obs-navbar-wrap.scrolled .navbar-brand img {
                        height: 45px;
                    }
                    .products-mega {
                        min-width: 300px;
                    }
                }

                @media (max-width: 991.98px) {
                    .obs-navbar {
                        height: 80px;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar {
                        height: 68px;
                    }
                    .collapse.navbar-collapse {
                        background: #0f172a;
                        position: absolute;
                        top: 100%;
                        left: 0;
                        right: 0;
                        padding: 1.25rem 1.5rem;
                        border-top: 1px solid rgba(255,255,255,0.1);
                        max-height: calc(100vh - 80px);
                        overflow-y: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                    .obs-navbar-wrap.scrolled .collapse.navbar-collapse {
                        background: #ffffff;
                        border-top: 1px solid #e5e7eb;
                    }
                    .obs-navbar .navbar-nav {
                        background: transparent !important;
                        gap: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .obs-navbar .obs-center-links {
                        gap: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        display: flex;
                        flex-direction: column;
                        background: transparent !important;
                    }
                    .obs-navbar .nav-item {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100%;
                    }
                    .obs-navbar .nav-link {
                        color: rgba(255,255,255,0.92) !important;
                        padding: 8px 0 !important;
                        display: block;
                        font-size: 0.95rem;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar .nav-link {
                        color: #1f2937 !important;
                    }
                    .obs-navbar .nav-link:hover {
                        color: #FBBF24 !important;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar .nav-link:hover {
                        color: #E52629 !important;
                    }

                    /* Products Dropdown nesting on Mobile */
                    .obs-navbar .dropdown-menu.products-mega {
                        position: static !important;
                        float: none;
                        width: 100%;
                        min-width: 100%;
                        box-shadow: none !important;
                        padding: 0.5rem 0 0.5rem 1rem !important; /* Indent mobile items */
                        margin-top: 0.25rem !important;
                        border: none !important;
                        background: rgba(255, 255, 255, 0.05) !important;
                        border-radius: 8px;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar .dropdown-menu.products-mega {
                        background: #f3f4f6 !important;
                    }

                    .obs-navbar .products-mega-item {
                        padding: 8px 10px !important;
                        gap: 10px;
                        background: transparent !important;
                    }
                    .obs-navbar .products-mega-icon {
                        width: 32px;
                        height: 32px;
                        min-width: 32px;
                        border-radius: 8px;
                    }
                    .obs-navbar .products-mega-icon svg {
                        width: 14px;
                        height: 14px;
                    }
                    .obs-navbar .products-mega-title {
                        font-size: 0.88rem;
                        color: #ffffff !important;
                        margin-bottom: 0px;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar .products-mega-title {
                        color: #1f2937 !important;
                    }
                    .obs-navbar .products-mega-desc {
                        font-size: 0.72rem;
                        color: rgba(255, 255, 255, 0.6) !important;
                    }
                    .obs-navbar-wrap.scrolled .obs-navbar .products-mega-desc {
                        color: #6b7280 !important;
                    }
                    .obs-navbar .products-mega-arrow {
                        display: none; /* Hide arrow on mobile */
                    }

                    .obs-utility-pill {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 10px;
                        border-radius: 14px;
                        margin-top: 0.75rem;
                        width: 100%;
                        padding: 10px 14px;
                    }
                    .obs-utility-divider {
                        display: none;
                    }
                    .obs-cta-group {
                        flex-direction: column;
                        width: 100%;
                        margin-top: 0.75rem;
                        gap: 10px !important;
                    }
                    .op-demo-btn, .op-trial-btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            <div className={`obs-navbar-wrap ${scrolled ? 'scrolled' : ''}`}>
                <nav className="navbar navbar-expand-lg w-100 obs-navbar" ref={navRef}>
                    <div className="container-fluid d-flex justify-content-between align-items-center">

                        {/* Logo */}
                        <a className="navbar-brand" href="https://orientalbiz.ca/">
                            <img src={Logo} alt="OBS logo" />
                        </a>

                        <button
                            className="navbar-toggler"
                            type="button"
                            onClick={handleToggleClick}
                            aria-controls="navbarNav"
                            aria-expanded={isOpen}
                            aria-label="Toggle navigation"
                        >
                            <span className="navbar-toggler-icon"></span>
                        </button>

                        <div
                            className={`collapse navbar-collapse justify-content-end ${isOpen ? 'show' : ''}`}
                            id="navbarNav"
                            ref={navbarCollapseRef}
                        >
                            {/* Center nav links - your own menu.
                                obs-center-links is what gets absolutely
                                centered in the navbar on desktop (see CSS
                                above); on mobile it just behaves like a
                                normal stacked list. */}
                            <ul className="navbar-nav align-items-lg-center obs-center-links">
                                {
                                    navbarData.slice(0, 3).map((navbar) => (
                                        <li className="nav-item" key={navbar.id}>
                                            <a
                                                className="nav-link"
                                                href={`https://orientalbiz.ca${navbar.path === '/' ? '' : navbar.path}`}
                                                onClick={handleLinkClick}
                                            >
                                                {navbar.title}
                                            </a>
                                        </li>
                                    ))
                                }

                                {/* Products mega-menu dropdown */}
                                <li className={`nav-item dropdown ${isDropdownOpen ? 'show' : ''}`}>
                                    <button
                                        className="nav-link dropdown-toggle"
                                        type="button"
                                        id="productsDropdown"
                                        style={{ background: 'none', border: 'none' }}
                                        onClick={() => setIsDropdownOpen(prev => !prev)}
                                        aria-expanded={isDropdownOpen}
                                    >
                                        Products
                                    </button>
                                    <ul className={`dropdown-menu products-mega ${isDropdownOpen ? 'show' : ''}`} aria-labelledby="productsDropdown">
                                        {
                                            productsData.map((product) => (
                                                <li key={product.id}>
                                                    <a
                                                        className="products-mega-item"
                                                         href={`https://orientalbiz.ca${product.path}`}
                                                        onClick={handleLinkClick}
                                                    >
                                                        <span
                                                            className="products-mega-icon"
                                                            style={{ background: product.accent }}
                                                        >
                                                            {product.icon}
                                                        </span>
                                                        <span>
                                                            <span className="d-block products-mega-title">
                                                                {product.title}
                                                            </span>
                                                            <span className="d-block products-mega-desc">
                                                                {product.desc}
                                                            </span>
                                                        </span>
                                                        <FaChevronRight size={12} className="products-mega-arrow" />
                                                    </a>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </li>

                                {
                                    navbarData.slice(3).map((navbar) => (
                                        <li className="nav-item" key={navbar.id}>
                                            <a
                                                className="nav-link"
                                                href={`https://orientalbiz.ca${navbar.path}`}
                                                onClick={handleLinkClick}
                                            >
                                                {navbar.title}
                                            </a>
                                        </li>
                                    ))
                                }
                            </ul>

                           
                        </div>

                    </div>
                </nav>
            </div>

            {/* Spacer: pushes the rest of the page down by exactly the
                navbar's current rendered height, since the navbar itself is
                position: fixed and out of document flow. Updates live as the
                navbar height changes (scroll state, breakpoint, resize). */}
            <div style={{ height: navHeight }} aria-hidden="true" />
        </>
    )
}

export default Navbar;